import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForUpdate, newId } from '../engine/context'
import { type SubmitWorkflowPayload, setSubmission, WorkflowError } from '../engine/workflowEngine'
import { deleteRow, findRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { notify, wmsRoute } from './notification.shared'
import { toErrorResponse } from './workflow.handler'

/**
 * Endpoint custom C04 Inbound WH RM (IRM-04 QC Scheduler, IRM-06 Putaway Monitoring) yang belum ada
 * backend-nya. Route mengikuti task BE M01.C04.P01.F01/F02 dan P03.F01:
 * - `GET  /api/v1/wms/inbound-rm/quarantine-summary` — antrian QC dikelompokkan per pallet + ringkasan KPI;
 * - `POST /api/v1/wms/qc-scheduler/workflow/qc-task-scheduler-modal` — WORKFLOW-CUSTOM WF-QC-01
 *   (`ACT_QC_SCHEDULE`): tanpa `submission_id` = buat assignment baru; dengan `submission_id` = alihkan operator;
 * - `GET  /api/v1/wms/inbound-rm/putaway-progress-summary` — progres per tugas putaway lintas operator.
 * Nomor assignment `QSC-YYMMDD-nnn` (prototype v7.44). Warning tier karantina mengikuti `qcWarnLevel`
 * prototype / BA §IRM-04 (>3 hari L1, >5 hari L2, >7 hari L3) — emulasi SLA Standalone `rm_quarantine_entry`.
 */

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')

const ok = (data: unknown) => [200, { statusCode: 200, success: true, data }]

function fail(message: string, code = 'ERR_VALIDATION_FAILED', status = 400): never {
  throw new WorkflowError(message, code, status)
}

const pad = (value: number, width: number) => String(value).padStart(width, '0')

/** Tanggal lokal `YYYY-MM-DD` (zona browser = WIB untuk pengguna JAL). */
export const localDate = (value: Date | string = new Date()) => {
  const date = typeof value === 'string' ? new Date(value) : value
  return `${date.getFullYear()}-${pad(date.getMonth() + 1, 2)}-${pad(date.getDate(), 2)}`
}

const dayNumber = (isoDate: string) => Math.round(new Date(`${isoDate}T00:00:00Z`).getTime() / 86_400_000)

/** Warning tier karantina (prototype `JalCalc.qcWarnLevel`). */
export function warningOf(enteredAt: unknown, today = localDate()) {
  const days = enteredAt ? dayNumber(today) - dayNumber(localDate(String(enteredAt))) : 0
  const level = days > 7 ? 3 : days > 5 ? 2 : days > 3 ? 1 : 0
  return { level, days }
}

/** Urutan tampil WF-QC-01 — v7.50 menambah `QC_PENDING_APPROVAL` (hasil disimpan, menunggu Admin WH RM). */
const QC_STATUS_CODES = ['QC_ASSIGNED', 'QC_IN_PROGRESS', 'QC_PENDING_APPROVAL', 'QC_DONE', 'QC_IN_STORAGE'] as const
type QcStatusCode = (typeof QC_STATUS_CODES)[number]

const QC_STATUS_COLUMN: Record<QcStatusCode, string> = {
  QC_ASSIGNED: 'ASSIGNED',
  QC_IN_PROGRESS: 'IN_PROGRESS',
  QC_PENDING_APPROVAL: 'PENDING_APPROVAL',
  QC_DONE: 'QC_DONE',
  QC_IN_STORAGE: 'IN_STORAGE'
}

const DURATIONS = [30, 60, 90, 120]

/* ------------------------------------------------------------ referensi */

const userRef = (userId: unknown) => {
  const user = findRow('directus_users', idOf(userId))
  return user ? { id: user.id, first_name: user.first_name, last_name: user.last_name ?? null } : null
}

const fullName = (userId: unknown) => {
  const user = userRef(userId)
  return user ? [user.first_name, user.last_name].filter(Boolean).join(' ') : '—'
}

function roleCodesOf(userId: string): string[] {
  return readTable('mt_privileges')
    .filter(row => idOf(row.user) === userId)
    .map(row => String(findRow('mt_roles', idOf(row.role))?.code ?? ''))
}

const statusCodeOf = (assignment: LocalRow): QcStatusCode => {
  const code = String(findRow('wml_status', idOf(assignment.status_id))?.code ?? '')
  if ((QC_STATUS_CODES as readonly string[]).includes(code)) return code as QcStatusCode
  const fromColumn = Object.entries(QC_STATUS_COLUMN).find(([, column]) => column === assignment.qc_status)
  return (fromColumn?.[0] as QcStatusCode | undefined) ?? 'QC_ASSIGNED'
}

const assignmentsOf = (entryId: string) =>
  readTable('rm_qc_assignment').filter(row => idOf(row.quarantine_entry_id) === entryId)

const scheduledQtyOf = (entryId: string) => assignmentsOf(entryId).reduce((sum, row) => sum + Number(row.qty ?? 0), 0)

function entryContext(entry: LocalRow) {
  const line = findRow('rm_arrival_line', idOf(entry.arrival_line_id))
  const material = line ? findRow('mt_material', idOf(line.material_id)) : undefined
  const unit = line ? findRow('mt_unit', idOf(line.unit_id)) : undefined
  const pallet = entry.pallet_id ? findRow('pallet', idOf(entry.pallet_id)) : undefined
  return { line, material, unit, pallet }
}

function assignmentRow(assignment: LocalRow) {
  const code = statusCodeOf(assignment)
  const status = findRow('wml_status', idOf(assignment.status_id))
  const unit = assignment.unit_id ? findRow('rm_unit', idOf(assignment.unit_id)) : undefined
  return {
    id: assignment.id,
    assignment_no: assignment.assignment_no,
    operator_id: userRef(assignment.operator_id),
    qty: Number(assignment.qty ?? 0),
    scheduled_date: assignment.scheduled_date,
    start_time: assignment.start_time,
    duration_min: Number(assignment.duration_min ?? 60),
    status: { code, name: status?.name ?? code, color: status?.color ?? null },
    submission_id: assignment.submission_id ?? null,
    unit_qr: unit?.qr_code ?? null
  }
}

function entryRow(entry: LocalRow, today: string) {
  const { line, material, unit, pallet } = entryContext(entry)
  const assignments = assignmentsOf(entry.id)
    .sort((a, b) => String(a.assignment_no).localeCompare(String(b.assignment_no)))
    .map(assignmentRow)
  const qty = Number(entry.qty ?? 0)
  const scheduled = assignments.reduce((sum, row) => sum + row.qty, 0)
  const qtyByStatus = Object.fromEntries(QC_STATUS_CODES.map(code => [code, 0])) as Record<QcStatusCode, number>
  assignments.forEach(row => {
    qtyByStatus[row.status.code] += row.qty
  })
  return {
    id: entry.id,
    quarantine_no: entry.quarantine_no,
    pallet: pallet ? { id: pallet.id, code: pallet.code } : null,
    material: material ? { id: material.id, code: material.code, name: material.name } : null,
    unit: unit?.name ?? '',
    batch_no: line?.batch_no ?? '',
    qty,
    entered_at: entry.entered_at,
    warning: warningOf(entry.entered_at, today),
    scheduled_qty: scheduled,
    remaining: Math.max(0, qty - scheduled),
    qty_by_status: qtyByStatus,
    is_qc_completed: Boolean(entry.is_qc_completed),
    assignments
  }
}

type EntryRow = ReturnType<typeof entryRow>

/** Satu antrian = satu pallet (prototype v4.8); entry tanpa pallet berdiri sendiri. */
function groupEntries(entries: EntryRow[]) {
  const groups: { id: string; pallet: EntryRow['pallet']; entries: EntryRow[] }[] = []
  const index = new Map<string, (typeof groups)[number]>()
  entries.forEach(entry => {
    const key = entry.pallet?.id ?? `QRT:${entry.id}`
    let group = index.get(key)
    if (!group) {
      group = { id: key, pallet: entry.pallet, entries: [] }
      index.set(key, group)
      groups.push(group)
    }
    group.entries.push(entry)
  })
  return groups.map(group => {
    const units = [...new Set(group.entries.map(entry => entry.unit))].map(unit => ({
      unit,
      qty: group.entries.filter(entry => entry.unit === unit).reduce((sum, entry) => sum + entry.qty, 0)
    }))
    const worst = group.entries.reduce((max, entry) => (entry.warning.level > max.level ? entry.warning : max), {
      level: -1,
      days: 0
    })
    return {
      ...group,
      units,
      total_qty: group.entries.reduce((sum, entry) => sum + entry.qty, 0),
      scheduled_qty: group.entries.reduce((sum, entry) => sum + entry.scheduled_qty, 0),
      remaining: group.entries.reduce((sum, entry) => sum + entry.remaining, 0),
      warning: worst
    }
  })
}

const paginate = <T>(rows: T[], params: Record<string, unknown>) => {
  const page = Math.max(1, Number(params.page ?? 1))
  const limit = Number(params.limit ?? 25)
  const items = limit > 0 ? rows.slice((page - 1) * limit, page * limit) : rows
  return { items, meta: { total: rows.length, page, limit } }
}

/** `GET /api/v1/wms/inbound-rm/quarantine-summary` — antrian karantina (belum tuntas QC) per pallet. */
export function quarantineSummary(params: Record<string, unknown> = {}) {
  const today = String(params.today ?? '') || localDate()
  const search = String(params.search ?? '')
    .trim()
    .toLowerCase()
  const tier = params.tier === undefined || params.tier === '' ? null : Number(params.tier)

  const queue = readTable('rm_quarantine_entry')
    .filter(entry => !entry.is_qc_completed)
    .map(entry => entryRow(entry, today))
    .sort((a, b) => String(a.entered_at).localeCompare(String(b.entered_at)))

  const filtered = queue.filter(entry => {
    if (tier !== null && entry.warning.level !== tier) return false
    if (!search) return true
    return [entry.quarantine_no, entry.material?.code, entry.material?.name, entry.batch_no, entry.pallet?.code]
      .map(value => String(value ?? '').toLowerCase())
      .some(value => value.includes(search))
  })

  const assignments = readTable('rm_qc_assignment')
  const countStatus = (code: QcStatusCode) => assignments.filter(row => statusCodeOf(row) === code).length

  return {
    ...paginate(groupEntries(filtered), params),
    summary: {
      unscheduled: queue.filter(entry => entry.remaining > 0).length,
      assigned: countStatus('QC_ASSIGNED'),
      in_progress: countStatus('QC_IN_PROGRESS'),
      pending_approval: countStatus('QC_PENDING_APPROVAL'),
      done_today: assignments.filter(
        row =>
          (statusCodeOf(row) === 'QC_DONE' || statusCodeOf(row) === 'QC_IN_STORAGE') && row.scheduled_date === today
      ).length,
      queue_rows: queue.length,
      filtered_rows: filtered.length,
      history_rows: readTable('rm_quarantine_entry').filter(entry => entry.is_qc_completed).length
    }
  }
}

/* -------------------------------------------------------- penjadwalan */

type ScheduleLine = {
  quarantine_entry_id?: string
  operator_id?: string
  qty?: number
  scheduled_date?: string
  start_time?: string
  duration_min?: number
}

const hhmm = (value: unknown) => String(value ?? '').slice(0, 5)

function requireOperator(operatorId: unknown) {
  const user = findRow('directus_users', idOf(operatorId))
  if (!user || !roleCodesOf(user.id).includes('operator_wh_rm')) fail('Petugas QC harus Operator WH RM.')
  return user
}

/** `QSC-YYMMDD-nnn` — urut per hari pembuatan. */
function nextAssignmentNo(taken: string[]) {
  const today = localDate().slice(2).replaceAll('-', '')
  const pattern = new RegExp(`^QSC-${today}-(\\d{3})$`)
  const highest = [...readTable('rm_qc_assignment').map(row => String(row.assignment_no ?? '')), ...taken]
    .map(value => pattern.exec(value))
    .reduce((max, match) => (match ? Math.max(max, Number(match[1])) : max), 0)
  return `QSC-${today}-${pad(highest + 1, 3)}`
}

/** Validasi seluruh baris dulu (prototype `sc-confirm`), baru menulis — setara satu transaksi. */
function validateLines(lines: ScheduleLine[]) {
  if (!lines.length) fail('Pilih minimal satu pallet terlebih dahulu.')
  const byEntry = new Map<string, ScheduleLine[]>()
  lines.forEach(line => {
    const key = String(line.quarantine_entry_id ?? '')
    byEntry.set(key, [...(byEntry.get(key) ?? []), line])
  })
  byEntry.forEach((entryLines, entryId) => {
    const entry = findRow('rm_quarantine_entry', entryId)
    if (!entry) fail('Baris karantina tidak ditemukan.')
    const qrt = String(entry.quarantine_no)
    if (entry.is_qc_completed) fail(`${qrt}: QC sudah tuntas.`)
    const remaining = Number(entry.qty ?? 0) - scheduledQtyOf(entryId)
    const seen = new Set<string>()
    let total = 0
    entryLines.forEach(line => {
      const operator = requireOperator(line.operator_id)
      const qty = Number(line.qty)
      if (!Number.isInteger(qty) || qty < 1) fail(`${qrt}: qty tiap operator minimal 1`)
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(line.scheduled_date ?? '')) || !/^\d{2}:\d{2}/.test(hhmm(line.start_time)))
        fail(`${qrt}: tanggal dan jam mulai wajib diisi`)
      if (!DURATIONS.includes(Number(line.duration_min)))
        fail(`${qrt}: durasi harus 30 mnt, 1 jam, 1,5 jam, atau 2 jam`)
      const slot = `${operator.id}|${line.scheduled_date}|${hhmm(line.start_time)}`
      if (seen.has(slot)) fail(`${qrt}: ${fullName(operator.id)} terjadwal dua kali di jam yang sama`)
      seen.add(slot)
      total += qty
    })
    if (total > remaining) fail(`${qrt}: total ${total} melebihi sisa ${remaining}`)
  })
}

function createAssignments(body: SubmitWorkflowPayload) {
  const lines = ((body.native_document_payload?.payload ?? {}) as { lines?: ScheduleLine[] }).lines ?? []
  validateLines(lines)

  const created: { id: string; assignment_no: string; operator: string; submission_id: string }[] = []
  try {
    lines.forEach(line => {
      const id = newId()
      const assignmentNo = nextAssignmentNo(created.map(row => row.assignment_no))
      const result = setSubmission({
        ...body,
        submission_id: null,
        entity_id: id,
        reason_text: body.reason_text ?? '',
        native_document_payload: {
          path: 'rm_qc_assignment',
          payload: {
            id,
            assignment_no: assignmentNo,
            quarantine_entry_id: line.quarantine_entry_id,
            operator_id: idOf(line.operator_id),
            qty: Number(line.qty),
            scheduled_date: line.scheduled_date,
            start_time: `${hhmm(line.start_time)}:00`,
            duration_min: Number(line.duration_min),
            qc_status: QC_STATUS_COLUMN.QC_ASSIGNED,
            actual_start: null,
            done_at: null,
            unit_id: null
          }
        }
      })
      created.push({
        id,
        assignment_no: assignmentNo,
        operator: idOf(line.operator_id),
        submission_id: result.submission_id
      })
    })
  } catch (error) {
    created.forEach(row => {
      deleteRow('rm_qc_assignment', row.id)
      readTable('wxl_submission_log')
        .filter(log => idOf(log.submission_id) === row.submission_id)
        .forEach(log => deleteRow('wxl_submission_log', log.id))
      deleteRow('wxl_submission', row.submission_id)
    })
    throw error
  }

  const perOperator = [...new Set(created.map(row => row.operator))].map(operator => ({
    operator_id: operator,
    name: fullName(operator),
    count: created.filter(row => row.operator === operator).length
  }))
  /** QC dijadwalkan → tiap Operator WH RM mendapat satu notifikasi ringkas jumlah tugasnya. */
  perOperator.forEach(part =>
    notify({
      to: { userIds: [part.operator_id] },
      type: 'QC_ASSIGNED',
      title: `${part.count} tugas QC baru untuk Anda`,
      body: `Admin WH RM menjadwalkan ${part.count} tugas QC barang karantina — buka QC RM — Operator.`,
      link: wmsRoute('inbound-rm/qc-rm-operator'),
      ref: { type: 'rm_qc_assignment', id: created.find(row => row.operator === part.operator_id)?.id ?? '' }
    })
  )
  return {
    count: created.length,
    assignment_nos: created.map(row => row.assignment_no),
    submission_ids: created.map(row => row.submission_id),
    per_operator: perOperator
  }
}

/** Alihkan operator assignment yang masih Assigned (JAL_04 §4 aturan 6) — `ACT_QC_SCHEDULE` self-loop. */
function reassignOperator(body: SubmitWorkflowPayload) {
  const submission = findRow('wxl_submission', body.submission_id)
  const assignment = submission ? findRow('rm_qc_assignment', idOf(submission.entity_id)) : undefined
  if (!assignment) fail('Assignment tidak ditemukan.', 'SUBMISSION_NOT_FOUND', 404)
  if (statusCodeOf(assignment) !== 'QC_ASSIGNED')
    fail('Hanya assignment berstatus Assigned yang bisa dialihkan.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  const operatorId = idOf((body.native_document_payload?.payload ?? {}).operator_id)
  requireOperator(operatorId)
  if (operatorId === idOf(assignment.operator_id)) fail('Operator tujuan sama dengan operator saat ini.')
  const clash = readTable('rm_qc_assignment').some(
    row =>
      row.id !== assignment.id &&
      idOf(row.quarantine_entry_id) === idOf(assignment.quarantine_entry_id) &&
      idOf(row.operator_id) === operatorId &&
      row.scheduled_date === assignment.scheduled_date &&
      hhmm(row.start_time) === hhmm(assignment.start_time)
  )
  if (clash) fail(`${fullName(operatorId)} sudah terjadwal di jam yang sama untuk baris ini.`)

  const from = fullName(assignment.operator_id)
  const result = setSubmission({
    ...body,
    reason_text: body.reason_text || `Dialihkan dari ${from} ke ${fullName(operatorId)}`,
    native_document_payload: { path: 'rm_qc_assignment', payload: { operator_id: operatorId } }
  })
  notify({
    to: { userIds: [operatorId] },
    type: 'QC_ASSIGNED',
    title: `Tugas QC ${assignment.assignment_no} dialihkan ke Anda`,
    body: `Tugas QC ${assignment.assignment_no} dialihkan dari ${from} — buka QC RM — Operator.`,
    link: wmsRoute('inbound-rm/qc-rm-operator'),
    ref: { type: 'rm_qc_assignment', id: String(assignment.id) }
  })
  return { ...result, assignment_no: assignment.assignment_no, from, to: fullName(operatorId) }
}

export function scheduleQcTask(body: SubmitWorkflowPayload) {
  return body.submission_id ? reassignOperator(body) : createAssignments(body)
}

/* ------------------------------------------------------------- putaway */

type ItemProgress = { done: boolean }

/* Item selesai begitu ditaruh (qty_placed > 0) — kekurangan dicatat di `qty_short` [ERD-GAP], bukan item terbuka
   (dok. 06 ERD-18, prototype v7.11 "selisihnya tercatat"; sama dengan `isPlaced` di inboundRmOperator.handler). */
const itemDone = (item: LocalRow): boolean => Number(item.qty_placed ?? 0) > 0

/** `GET /api/v1/wms/inbound-rm/putaway-progress-summary` — satu baris per tugas (operator) putaway. */
export function putawayProgressSummary(params: Record<string, unknown> = {}) {
  const search = String(params.search ?? '')
    .trim()
    .toLowerCase()
  const tasks = readTable('rm_putaway_task')
  const items = readTable('rm_putaway_task_item')
  const rows = tasks
    .map(task => {
      const own: ItemProgress[] = items
        .filter(item => idOf(item.task_id) === task.id)
        .map(item => ({
          done: itemDone(item)
        }))
      const done = own.filter(item => item.done).length
      const total = own.length
      const siblings = tasks
        .filter(other => other.task_no === task.task_no)
        .sort((a, b) => String(a.id).localeCompare(String(b.id)))
      return {
        id: task.id,
        task_no: String(task.task_no),
        task_seq: siblings.findIndex(other => other.id === task.id) + 1,
        operator_id: userRef(task.operator_id),
        done,
        total,
        progress_status: total > 0 && done === total ? 'COMPLETED' : done > 0 ? 'IN_PROGRESS' : 'ASSIGNED',
        date_created: task.date_created
      }
    })
    .sort((a, b) => b.task_no.localeCompare(a.task_no) || a.task_seq - b.task_seq)
  const filtered = search
    ? rows.filter(row =>
        [row.task_no, fullName(row.operator_id?.id)].some(value => value.toLowerCase().includes(search))
      )
    : rows
  return {
    ...paginate(filtered, params),
    summary: {
      total: rows.length,
      assigned: rows.filter(row => row.progress_status === 'ASSIGNED').length,
      in_progress: rows.filter(row => row.progress_status === 'IN_PROGRESS').length,
      completed: rows.filter(row => row.progress_status === 'COMPLETED').length
    }
  }
}

/* -------------------------------------------------------------- routes */

const parse = <T>(data: unknown) => JSON.parse(String(data ?? '{}')) as T

const handle = (run: () => unknown) => {
  try {
    return ok(run())
  } catch (error) {
    return toErrorResponse(error)
  }
}

/** Dipakai test: tandai satu item putaway sudah ditaruh (eksekusi IRM-07 belum dimigrasi). */
export function placePutawayItem(itemId: string, qty: number, actualRackBinId?: string, reason?: string) {
  return updateRow('rm_putaway_task_item', itemId, {
    qty_placed: qty,
    actual_rack_bin_id: actualRackBinId ?? null,
    override_reason: reason ?? null,
    ...auditFieldsForUpdate()
  })
}

export function registerInboundRmHandlers(mock: MockAdapter) {
  mock
    .onGet('/api/v1/wms/inbound-rm/quarantine-summary')
    .reply(config => handle(() => quarantineSummary((config.params ?? {}) as Record<string, unknown>)))
  mock
    .onPost('/api/v1/wms/qc-scheduler/workflow/qc-task-scheduler-modal')
    .reply(config => handle(() => scheduleQcTask(parse(config.data))))
  mock
    .onGet('/api/v1/wms/inbound-rm/putaway-progress-summary')
    .reply(config => handle(() => putawayProgressSummary((config.params ?? {}) as Record<string, unknown>)))
}
